import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Film, Fingerprint, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { FollowButton } from "@/components/social/FollowButton";

function Avatar({ url, name, size = 32 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        return <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>{initial}</div>
    );
}

/** Global search overlay — cases, media archive, species and members. */
export function GlobalSearch({ open, onClose }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (open) {
            setQ("");
            setResults(null);
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    function onChange(v) {
        setQ(v);
        clearTimeout(timer.current);
        if (v.trim().length < 2) { setResults(null); return; }
        setLoading(true);
        timer.current = setTimeout(() => {
            api.get("/search", { params: { q: v.trim() } })
                .then((r) => setResults(r.data))
                .finally(() => setLoading(false));
        }, 250);
    }

    function go(path) {
        onClose();
        navigate(path);
    }

    if (!open) return null;
    const empty = results && !results.cases.length && !results.media.length && !results.species.length && !results.users.length;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[8vh] px-4" data-testid="global-search-overlay">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="global-search-backdrop" />
            <div className="relative w-full max-w-2xl ok-card p-0 overflow-hidden shadow-2xl" style={{ background: "var(--ok-surface)" }}>
                <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--ok-border)" }}>
                    <Search className="w-4 h-4 opacity-50" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Search cases, media, species, members…"
                        className="flex-1 bg-transparent outline-none text-sm"
                        data-testid="global-search-input"
                    />
                    <button onClick={onClose} className="p-1 opacity-50 hover:opacity-100" data-testid="global-search-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="global-search-results">
                    {loading && <div className="px-3 py-4 font-mono text-[10px] uppercase tracking-widest opacity-50">Searching the archive…</div>}
                    {!loading && !results && <div className="px-3 py-4 font-mono text-[10px] uppercase tracking-widest opacity-40">Type at least 2 characters</div>}
                    {!loading && empty && <div className="px-3 py-4 text-sm opacity-60" data-testid="global-search-empty">Nothing in the archive matches “{q}”.</div>}

                    {results?.users?.length > 0 && (
                        <Section label="Members">
                            {results.users.map((u) => (
                                <div key={u.id} onClick={() => go(`/u/${u.id}`)} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--ok-surface-2)] cursor-pointer" data-testid={`search-user-${u.id}`}>
                                    <Avatar url={u.avatar_url} name={u.name} size={34} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm flex items-center gap-2">
                                            <span className="truncate">{u.name}</span>
                                            {u.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                                            {u.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                                        </div>
                                        {u.bio && <div className="text-xs opacity-60 truncate">{u.bio.split("\n")[0]}</div>}
                                    </div>
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <FollowButton userId={u.id} />
                                        {user && user.id !== u.id && (
                                            <button onClick={() => go(`/messages/${u.id}`)} className="ok-btn-outline text-xs inline-flex items-center gap-1" data-testid={`search-message-${u.id}`}>
                                                <Send className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </Section>
                    )}
                    {results?.cases?.length > 0 && (
                        <Section label="Cases">
                            {results.cases.map((c) => (
                                <div key={c.id} onClick={() => go(`/encounters/${c.id}`)} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--ok-surface-2)] cursor-pointer" data-testid={`search-case-${c.id}`}>
                                    <FileText className="w-4 h-4 opacity-50 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm truncate">{c.title}</div>
                                        <div className="text-xs opacity-60 truncate">{[c.date, c.location].filter(Boolean).join(" · ")}</div>
                                    </div>
                                </div>
                            ))}
                        </Section>
                    )}
                    {results?.media?.length > 0 && (
                        <Section label="Media Archive">
                            {results.media.map((m) => (
                                <div key={m.slug} onClick={() => go(`/media-archive/${m.slug}`)} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--ok-surface-2)] cursor-pointer" data-testid={`search-media-${m.slug}`}>
                                    <Film className="w-4 h-4 opacity-50 flex-shrink-0" />
                                    <div className="text-sm truncate">{m.title}</div>
                                </div>
                            ))}
                        </Section>
                    )}
                    {results?.species?.length > 0 && (
                        <Section label="Species">
                            {results.species.map((s) => (
                                <div key={s.slug} onClick={() => go(`/species/${s.slug}`)} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--ok-surface-2)] cursor-pointer" data-testid={`search-species-${s.slug}`}>
                                    <Fingerprint className="w-4 h-4 opacity-50 flex-shrink-0" />
                                    <div className="text-sm truncate">{s.name}</div>
                                </div>
                            ))}
                        </Section>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ label, children }) {
    return (
        <div className="mb-1">
            <div className="px-3 pt-2 pb-1 font-mono text-[9px] uppercase tracking-[0.3em] text-[var(--ok-accent)]">{label}</div>
            {children}
        </div>
    );
}
